import React from 'react'
import { ExternalLink, Lock, Clock, Mic } from 'lucide-react'
import HeatBadge from './HeatBadge'
import CoverageBadge from './CoverageBadge'
import ExpandableSummary from './ExpandableSummary'

const categoryConfig = {
  markets: { label: 'MARKETS', color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' },
  tech: { label: 'TECH', color: 'text-violet-400', bg: 'bg-violet-500/10', border: 'border-violet-500/30' },
  world: { label: 'WORLD', color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/30' },
  sports: { label: 'SPORTS', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
  popculture: { label: 'CULTURE', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  general: { label: 'NEWS', color: 'text-gray-400', bg: 'bg-gray-500/10', border: 'border-gray-500/30' }
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return ''
  const age = (Date.now() - new Date(dateStr).getTime()) / 60000
  if (age < 15) return 'Just now'
  if (age < 60) return `${Math.floor(age)}m ago`
  if (age < 1440) return `${Math.floor(age / 60)}h ago`
  return 'Yesterday'
}

function FreeLinks({ links }) {
  if (!links || links.length === 0) return null
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {links.slice(0, 3).map((link, i) => (
        <a
          key={i}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`
            inline-flex items-center gap-1 rounded text-[11px] md:text-[10px] px-2.5 py-1.5 md:px-2 md:py-1
            font-medium uppercase tracking-wider transition-colors
            ${link.is_paywalled
              ? 'text-gray-600 active:text-gray-400 bg-white/5'
              : 'text-gray-400 active:text-emerald-400 bg-white/5 md:hover:bg-white/10'
            }
          `}
        >
          {link.is_paywalled && <Lock className="w-3 h-3 md:w-2.5 md:h-2.5" />}
          {link.name}
          <ExternalLink className="w-3 h-3 md:w-2.5 md:h-2.5 opacity-50" />
        </a>
      ))}
    </div>
  )
}

export default function HeroCard({ story, maxHeat }) {
  const cat = categoryConfig[story.category] || categoryConfig.general
  const headline = story.headline || story.title
  const isBreaking = headline?.toLowerCase().includes('breaking')

  return (
    <article className="relative group rounded-none md:rounded-2xl overflow-hidden bg-gray-900 border-y md:border border-white/10 -mx-3 md:mx-0 active:bg-gray-800/80 md:hover:border-white/20 transition-colors">
      <div className="p-4 md:p-8">
        {/* Top badges - scrollable on mobile */}
        <div className="flex items-center gap-2 mb-3 md:mb-4 overflow-x-auto scrollbar-hide -mx-1 px-1">
          <span className={`text-[11px] md:text-[10px] font-bold tracking-[0.12em] px-2.5 py-1.5 md:py-1 rounded-full ${cat.bg} ${cat.color} border ${cat.border} whitespace-nowrap flex-shrink-0`}>
            {cat.label}
          </span>
          {isBreaking && (
            <span className="text-[11px] md:text-[10px] font-bold tracking-[0.12em] px-2.5 py-1.5 md:py-1 rounded-full bg-red-500/20 text-red-400 border border-red-500/30 animate-pulse whitespace-nowrap flex-shrink-0">
              BREAKING
            </span>
          )}
          {story.is_major && !isBreaking && (
            <span className="text-[11px] md:text-[10px] font-bold tracking-[0.12em] px-2.5 py-1.5 md:py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 whitespace-nowrap flex-shrink-0">
              #1 TRENDING
            </span>
          )}
          <CoverageBadge score={story.coverage_score} matches={story.reference_matches} />
          {story.podcast_mentions?.length > 0 && (
            <span className="text-[11px] md:text-[10px] text-fuchsia-400 flex items-center gap-1 whitespace-nowrap flex-shrink-0">
              <Mic className="w-3 h-3" />
              {story.podcast_mentions.join(', ')}
            </span>
          )}
          {story.first_seen && (
            <span className="text-[11px] md:text-[10px] text-gray-500 flex items-center gap-1 whitespace-nowrap flex-shrink-0">
              <Clock className="w-3 h-3" />
              {formatRelativeTime(story.first_seen)}
            </span>
          )}
        </div>

        <h2 className="text-xl md:text-2xl lg:text-3xl font-bold leading-tight text-white mb-2 md:mb-3">
          {headline}
        </h2>

        {story.summary && (
          <p className="text-[14px] md:text-sm text-gray-400 leading-relaxed mb-3 md:mb-4">
            {story.summary}
          </p>
        )}

        <ExpandableSummary bullets={story.bullet_points} />

        <div className="mt-3 md:mt-4 mb-3 md:mb-4">
          <HeatBadge score={story.heat_score} maxScore={maxHeat} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 md:gap-4 pt-3 md:pt-4 border-t border-white/5">
          <FreeLinks links={story.free_links} />
          {story.sources && (
            <span className="text-[10px] text-gray-600">
              {story.sources.map(s => s === 'google_news' ? 'Google News' : s).join(' + ')}
            </span>
          )}
        </div>
      </div>
    </article>
  )
}
